import React from 'react';
import { LocalDate } from '@js-joda/core';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OpacityIcon from '@mui/icons-material/Opacity';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { i18n } from 'i18n';
import { Model } from '../hooks/useModelState';
import { formatEpochDay, getStopeShapeDateDomain } from '../MineModel/Meshes/stopeShapeDateRange';
import { AnimationStep, getPourDateDomain } from '../MineModel/Meshes/stopePourTimeline';

interface StopeShapeDateRangeControlProps {
  model: Model;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

const epochDayToDayjs = (epochDay: number): Dayjs => dayjs(LocalDate.ofEpochDay(epochDay).toString());
const dayjsToEpochDay = (value: Dayjs): number => LocalDate.parse(value.format('YYYY-MM-DD')).toEpochDay();

// Advance an epoch day by one animation step (calendar-accurate for week/month).
const stepEpochDay = (epochDay: number, step: AnimationStep, direction: 1 | -1): number => {
  const date = LocalDate.ofEpochDay(epochDay);
  if (step === 'month') return date.plusMonths(direction).toEpochDay();
  if (step === 'week') return date.plusWeeks(direction).toEpochDay();
  return date.plusDays(direction).toEpochDay();
};

const PRESETS: { label: string; months: number | null }[] = [
  { label: i18n._('All'), months: null },
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
];

const presetStartEpochDay = (domainStart: number, domainEnd: number, months: number): number => Math.max(
  LocalDate.ofEpochDay(domainEnd).minusMonths(months).toEpochDay(),
  domainStart,
);

const MIN_FPS = 1;
const MAX_FPS = 60;

const StopeShapeDateRangeControl = ({ model }: StopeShapeDateRangeControlProps) => {
  const {
    stopeShapeDateRange,
    stopeShapeTimelineDay,
    stopeShapeOpacity,
    stopeShapeAnimationStep,
    setStopeShapeDateRange,
    setStopeShapeTimelineDay,
    setStopeShapeOpacity,
    setStopeShapeAnimationStep,
  } = model;
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [fps, setFps] = React.useState(10);
  const [opacityAnchor, setOpacityAnchor] = React.useState<HTMLElement | null>(null);
  const [settingsAnchor, setSettingsAnchor] = React.useState<HTMLElement | null>(null);

  const domain = React.useMemo(
    () => getPourDateDomain(model.stopePourData) ?? getStopeShapeDateDomain(model.stopes),
    [model.stopePourData, model.stopes],
  );

  const bounds = React.useMemo<[number, number] | null>(() => {
    if (!domain) return null;
    if (stopeShapeDateRange) {
      const start = clamp(stopeShapeDateRange.startEpochDay, domain.startEpochDay, domain.endEpochDay);
      const end = clamp(stopeShapeDateRange.endEpochDay, domain.startEpochDay, domain.endEpochDay);
      return start <= end ? [start, end] : [end, start];
    }
    return [domain.startEpochDay, domain.endEpochDay];
  }, [domain, stopeShapeDateRange]);

  const timelineValue = React.useMemo(() => {
    if (!bounds) return null;
    const [min, max] = bounds;
    return clamp(stopeShapeTimelineDay ?? max, min, max);
  }, [bounds, stopeShapeTimelineDay]);

  React.useEffect(() => {
    if (stopeShapeTimelineDay === null || !bounds || timelineValue === null) return;
    if (timelineValue !== stopeShapeTimelineDay) setStopeShapeTimelineDay(timelineValue);
  }, [bounds, setStopeShapeTimelineDay, stopeShapeTimelineDay, timelineValue]);

  React.useEffect(() => {
    if (!model.showStopeShapes || !isPlaying || !bounds || timelineValue === null) return undefined;

    const [min, max] = bounds;
    const intervalId = window.setInterval(() => {
      const next = stepEpochDay(timelineValue, stopeShapeAnimationStep, 1);
      setStopeShapeTimelineDay(next > max ? min : next);
    }, 1000 / clamp(fps, MIN_FPS, MAX_FPS));

    return () => window.clearInterval(intervalId);
  }, [bounds, fps, isPlaying, model.showStopeShapes, setStopeShapeTimelineDay, stopeShapeAnimationStep, timelineValue]);

  React.useEffect(() => {
    if (!model.showStopeShapes) setIsPlaying(false);
  }, [model.showStopeShapes]);

  const activePresetLabel = React.useMemo(() => {
    if (!stopeShapeDateRange) return 'All';
    if (!domain || stopeShapeDateRange.endEpochDay !== domain.endEpochDay) return null;
    return PRESETS.find(
      (preset) => preset.months !== null
        && presetStartEpochDay(domain.startEpochDay, domain.endEpochDay, preset.months) === stopeShapeDateRange.startEpochDay,
    )?.label ?? null;
  }, [domain, stopeShapeDateRange]);

  if (!model.showStopeShapes) return null;

  const hasTimeline = !!bounds && timelineValue !== null;
  const isTimelineActive = stopeShapeTimelineDay !== null;
  const opacityPercent = Math.round(stopeShapeOpacity * 100);
  const subtitle = hasTimeline && timelineValue !== null
    ? formatEpochDay(timelineValue)
    : i18n._('No stope pours');

  const jumpToStart = () => {
    setIsPlaying(false);
    if (bounds) setStopeShapeTimelineDay(bounds[0]);
  };

  const jumpToEnd = () => {
    setIsPlaying(false);
    if (bounds) setStopeShapeTimelineDay(bounds[1]);
  };

  const stepBy = (direction: 1 | -1) => {
    if (!bounds || timelineValue === null) return;
    setIsPlaying(false);
    const next = stepEpochDay(timelineValue, stopeShapeAnimationStep, direction);
    setStopeShapeTimelineDay(clamp(next, bounds[0], bounds[1]));
  };

  const togglePlay = () => {
    if (!bounds || timelineValue === null) return;
    if (!isPlaying && (stopeShapeTimelineDay === null || timelineValue >= bounds[1])) {
      setStopeShapeTimelineDay(bounds[0]);
    }
    setIsPlaying((current) => !current);
  };

  const handleSliderChange = (_event: Event, value: number | number[]) => {
    if (Array.isArray(value)) return;
    setIsPlaying(false);
    setStopeShapeTimelineDay(value);
  };

  const handleOpacityChange = (_event: Event, value: number | number[]) => {
    if (Array.isArray(value)) return;
    setStopeShapeOpacity(value / 100);
  };

  const handleStepChange = (_event: React.MouseEvent<HTMLElement>, value: AnimationStep | null) => {
    if (value) setStopeShapeAnimationStep(value);
  };

  const applyPreset = (months: number | null) => {
    if (months === null || !domain) {
      setStopeShapeDateRange(null);
      return;
    }
    setStopeShapeDateRange({
      startEpochDay: presetStartEpochDay(domain.startEpochDay, domain.endEpochDay, months),
      endEpochDay: domain.endEpochDay,
    });
  };

  const handleStartDateChange = (value: Dayjs | null) => {
    if (!value || !value.isValid() || !bounds) return;
    setStopeShapeDateRange({ startEpochDay: dayjsToEpochDay(value), endEpochDay: bounds[1] });
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    if (!value || !value.isValid() || !bounds) return;
    setStopeShapeDateRange({ startEpochDay: bounds[0], endEpochDay: dayjsToEpochDay(value) });
  };

  const transportSx = { color: 'common.white', '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.28)' } };

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        zIndex: 1500,
        width: { xs: 'calc(100% - 32px)', sm: 640 },
        maxWidth: 'calc(100% - 32px)',
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        bgcolor: 'rgba(40, 40, 40, 0.94)',
        color: 'common.white',
        backdropFilter: 'blur(8px)',
        display: 'grid',
        gridTemplateColumns: 'auto auto minmax(0, 1fr) auto auto auto',
        alignItems: 'center',
        columnGap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={i18n._('Jump to start')}>
          <span>
            <IconButton size="small" onClick={jumpToStart} disabled={!hasTimeline} sx={transportSx}>
              <FirstPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={i18n._('Step back')}>
          <span>
            <IconButton size="small" onClick={() => stepBy(-1)} disabled={!hasTimeline} sx={transportSx}>
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isPlaying ? i18n._('Pause') : i18n._('Play')}>
          <span>
            <IconButton size="small" onClick={togglePlay} disabled={!hasTimeline} sx={{ ...transportSx, color: '#39ff6b' }}>
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={i18n._('Step forward')}>
          <span>
            <IconButton size="small" onClick={() => stepBy(1)} disabled={!hasTimeline} sx={transportSx}>
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={i18n._('Jump to end')}>
          <span>
            <IconButton size="small" onClick={jumpToEnd} disabled={!hasTimeline} sx={transportSx}>
              <LastPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 88, textAlign: 'center' }}>
        {subtitle}
      </Typography>

      <Slider
        value={timelineValue ?? 0}
        min={bounds?.[0] ?? 0}
        max={bounds?.[1] ?? 1}
        step={1}
        disabled={!hasTimeline}
        onChange={handleSliderChange}
        valueLabelDisplay="auto"
        valueLabelFormat={(value) => formatEpochDay(value)}
        sx={{
          color: isTimelineActive ? '#1c7ed6' : 'rgba(255, 255, 255, 0.55)',
          '& .MuiSlider-rail': { color: 'rgba(255, 255, 255, 0.35)' },
          '& .MuiSlider-thumb': { bgcolor: 'common.white' },
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.72)' }}>F/S</Typography>
        <TextField
          type="number"
          value={fps}
          onChange={(event) => setFps(clamp(Number(event.target.value) || MIN_FPS, MIN_FPS, MAX_FPS))}
          size="small"
          inputProps={{ min: MIN_FPS, max: MAX_FPS, style: { width: 36, padding: '2px 4px', color: 'white', textAlign: 'center' } }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.35)' } }}
        />
      </Box>

      <Tooltip title={i18n._('Stope shape transparency')}>
        <IconButton size="small" onClick={(event) => setOpacityAnchor(event.currentTarget)} sx={transportSx}>
          <OpacityIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={i18n._('Timeline settings')}>
        <IconButton size="small" onClick={(event) => setSettingsAnchor(event.currentTarget)} sx={transportSx}>
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={!!opacityAnchor}
        anchorEl={opacityAnchor}
        onClose={() => setOpacityAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { bgcolor: 'rgba(40, 40, 40, 0.96)', p: 1.5, overflow: 'visible' } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, height: 160 }}>
          <Typography variant="caption" sx={{ color: 'common.white' }}>{`${opacityPercent}%`}</Typography>
          <Slider
            orientation="vertical"
            value={opacityPercent}
            min={0}
            max={100}
            onChange={handleOpacityChange}
            sx={{ color: '#1c7ed6', '& .MuiSlider-thumb': { bgcolor: 'common.white' } }}
          />
        </Box>
      </Popover>

      <Popover
        open={!!settingsAnchor}
        anchorEl={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { bgcolor: 'rgba(40, 40, 40, 0.96)', color: 'common.white', p: 2, width: 280 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{i18n._('Date range')}</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <DatePicker
                format="DD/MM/YYYY"
                label={i18n._('Start')}
                value={bounds ? epochDayToDayjs(bounds[0]) : null}
                onChange={handleStartDateChange}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                format="DD/MM/YYYY"
                label={i18n._('Finish')}
                value={bounds ? epochDayToDayjs(bounds[1]) : null}
                onChange={handleEndDateChange}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {PRESETS.map((preset) => (
              <ToggleButton
                key={preset.label}
                value={preset.label}
                size="small"
                selected={preset.label === activePresetLabel}
                onClick={() => applyPreset(preset.months)}
                sx={{ color: 'common.white', borderColor: 'rgba(255, 255, 255, 0.35)', px: 1, py: 0.25 }}
              >
                {preset.label}
              </ToggleButton>
            ))}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>{i18n._('Animation step')}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={stopeShapeAnimationStep}
            onChange={handleStepChange}
            sx={{ '& .MuiToggleButton-root': { color: 'common.white', borderColor: 'rgba(255, 255, 255, 0.35)' } }}
          >
            <ToggleButton value="day">{i18n._('Days')}</ToggleButton>
            <ToggleButton value="week">{i18n._('Weeks')}</ToggleButton>
            <ToggleButton value="month">{i18n._('Months')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Popover>
    </Paper>
  );
};

export default StopeShapeDateRangeControl;
